import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api';
import UniversalModal from '../Common/UniversalModal';
import Toast from '../Common/Toast';
import '../Common/CardActions.css';
import '../Common/PrimaryActionButton.css';
import './Creditors.css';

const emptyForm = {
  name: '',
};

const CreditorsList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);

  const [modalState, setModalState] = useState({ isOpen: false, mode: null, item: null }); // create|view|edit
  const [formData, setFormData] = useState(emptyForm);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Удалить',
    cancelText: 'Отмена'
  });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const all = [];
      let page = 1;
      while (true) {
        const resp = await apiService.getCreditors(`?page=${page}`);
        const chunk = resp.data.results || [];
        all.push(...chunk);
        if (!resp.data.next) break;
        page += 1;
        if (page > 200) break;
      }
      setItems(all);
    } catch (err) {
      setError('Ошибка при загрузке кредиторов: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return items;
    return (items || []).filter((x) => String(x?.name || '').toLowerCase().includes(q));
  }, [items, search]);

  const openCreate = () => {
    setFormData(emptyForm);
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'create', item: null });
  };

  const openView = (item) => {
    setFormData({ name: item?.name || '' });
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'view', item });
  };

  const openEdit = (item) => {
    setFormData({ name: item?.name || '' });
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'edit', item });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, mode: null, item: null });
    setFormData(emptyForm);
    setHasUnsavedChanges(false);
  };

  const onField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async ({ exit } = {}) => {
    try {
      let saved = null;
      if (modalState.item?.id) {
        const resp = await apiService.updateCreditor(modalState.item.id, formData);
        saved = resp.data;
        setItems((prev) => (prev || []).map((x) => (x.id === saved.id ? saved : x)));
        showToast('Изменения сохранены');
      } else {
        const resp = await apiService.createCreditor(formData);
        saved = resp.data;
        setItems((prev) => [saved, ...(prev || [])]);
        showToast('Кредитор добавлен');
      }
      setHasUnsavedChanges(false);
      if (exit) {
        closeModal();
      } else {
        openEdit(saved);
      }
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.name
        ? Array.isArray(data.name) ? data.name.join(', ') : data.name
        : (data?.detail || data?.error || err.message);
      showToast('Ошибка при сохранении кредитора: ' + msg, 'error');
    }
  };

  const handleDelete = async (item) => {
    showConfirmDialog(
      'Подтверждение удаления',
      `Удалить кредитора ${item?.name || ''}?`,
      async () => {
        try {
          await apiService.deleteCreditor(item.id);
          setItems((prev) => (prev || []).filter((x) => x.id !== item.id));
          showToast('Кредитор удалён');
        } catch (err) {
          const msg = err.response?.data?.detail || err.response?.data?.error || err.message;
          setError('Ошибка при удалении кредитора: ' + msg);
          showToast('Ошибка при удалении кредитора', 'error');
        }
      }
    );
  };

  const showConfirmDialog = (title, message, onConfirm, confirmText = 'Удалить') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      confirmText,
      cancelText: 'Отмена'
    });
  };

  const hideConfirmDialog = () => {
    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
  };

  const handleConfirmAction = () => {
    if (confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    hideConfirmDialog();
  };

  if (loading) {
    return <div style={{ padding: 50, textAlign: 'center' }}><h2>Загрузка кредиторов...</h2></div>;
  }

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1>🏦 Кредиторы</h1>
        <button
          type="button"
          onClick={openCreate}
          className="primary-create-button"
        >
          <span className="primary-create-button__icon">+</span>
          Добавить
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по наименованию..."
          style={{ flex: 1, minWidth: 260, padding: 10, border: '1px solid #dee2e6', borderRadius: 10 }}
        />
        <div style={{ color: '#6c757d', whiteSpace: 'nowrap' }}>
          Найдено: <b>{filtered.length}</b>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fee', border: '1px solid #fcc', color: '#a00', padding: 12, borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 12, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Наименование</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #e9ecef', width: 200 }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={2} style={{ padding: 20, textAlign: 'center', color: '#6c757d' }}>
                  Кредиторы не найдены
                </td>
              </tr>
            ) : filtered.map((x) => (
              <tr key={x.id} onClick={() => openView(x)} style={{ cursor: 'pointer' }}>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f3f5' }}>{x.name}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f3f5' }}>
                  <div className="card-actions" style={{ justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="card-action-btn card-action-btn--edit"
                      title="Редактировать"
                      onClick={(e) => { e.stopPropagation(); openEdit(x); }}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="card-action-btn card-action-btn--delete"
                      title="Удалить"
                      onClick={(e) => { e.stopPropagation(); handleDelete(x); }}
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalState.isOpen && (
        <UniversalModal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          title={
            modalState.mode === 'create' ? 'Добавить кредитора'
              : modalState.mode === 'edit' ? `Кредитор: ${modalState.item?.name || ''}`
                : `Кредитор: ${modalState.item?.name || ''}`
          }
          onSave={modalState.mode === 'view' ? null : () => handleSave({ exit: false })}
          onSaveAndExit={modalState.mode === 'view' ? null : () => handleSave({ exit: true })}
          onCancel={closeModal}
          hasUnsavedChanges={hasUnsavedChanges}
          isViewMode={modalState.mode === 'view'}
          onEdit={modalState.mode === 'view' ? () => openEdit(modalState.item) : null}
          disableEscClose={confirmDialog.isOpen}
        >
          <div className="creditors-modal" style={{ padding: 8 }}>
            <div className="form-group">
              <label>Наименование:</label>
              <input
                type="text"
                value={formData.name}
                readOnly={modalState.mode === 'view'}
                onChange={(e) => {
                  if (modalState.mode === 'view') return;
                  onField('name', e.target.value);
                }}
                placeholder={modalState.mode === 'view' ? '-' : 'Введите наименование кредитора'}
              />
            </div>
            {modalState.mode !== 'view' && (
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  marginTop: '32px',
                  paddingTop: '24px',
                  borderTop: '1px solid #e1e5e9'
                }}
              >
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={() => handleSave({ exit: false })}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Сохранить
                </button>
                <button
                  type="button"
                  onClick={() => handleSave({ exit: true })}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Сохранить и выйти
                </button>
              </div>
            )}
          </div>
        </UniversalModal>
      )}

      {confirmDialog.isOpen && (
        <ConfirmDialog
          confirmDialog={confirmDialog}
          onCancel={hideConfirmDialog}
          onConfirm={handleConfirmAction}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
};

const ConfirmDialog = ({ confirmDialog, onCancel, onConfirm }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        onCancel();
      }
    };

    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [onCancel]);

  return (
    <UniversalModal
      isOpen={!!confirmDialog?.isOpen}
      onClose={onCancel}
      title={confirmDialog?.title || 'Подтверждение'}
      message={confirmDialog?.message}
      onSave={onConfirm}
      onCancel={onCancel}
      cancelText={confirmDialog?.cancelText || 'Отмена'}
      confirmText={confirmDialog?.confirmText || 'Подтвердить'}
      showSaveAndExit={false}
      showEditButton={false}
      disableEscClose={false}
      hideDefaultCloseButton={true}
    >
      <div />
    </UniversalModal>
  );
};

export default CreditorsList;
